import { styled } from '@mui/material';
import * as React from 'react';
import type { JSX } from 'react';

import { colorSystem, focusVisibleRing } from '../../theme';
import { iconSlot } from '../icons/iconSlot';

export type ChipIconButtonProps = {
    /** Names the action and its chip, such as "Remove filter: Capacitance": the button shows only an icon. */
    'aria-label': string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** The icon, with no size: the button draws it at 14px. */
    children: React.ReactElement;
    className?: string;
};

/** An icon action at a chip's end, like the × that removes a filter; the icon buttons don't fit a 24px chip. */
export function ChipIconButton({ children, ...props }: ChipIconButtonProps): JSX.Element {
    return (
        <ChipIconButtonRoot {...props} type="button">
            {children}
        </ChipIconButtonRoot>
    );
}

const ChipIconButtonRoot = styled('button')(
    {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px',
        margin: 0,
        border: 'none',
        // The chip's 4px corner, inside its 1px border.
        borderRadius: '3px',
        background: 'none',
        color: colorSystem.neutral[6],
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background 0.2s linear',
        '&:hover': {
            background: colorSystem.neutral[2],
        },
        ...iconSlot(14),
    },
    focusVisibleRing,
    // Inside the button: outside, the ring would stick out of the 24px chip.
    { '&:focus-visible': { outlineOffset: '-2px' } },
);
